import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '@/api/client';

interface BOMItem {
    id: string;
    item_id: string;
    item_name?: string;
    item_code?: string;
    item_type?: string;
    quantity: number;
    unit_name?: string;
    unit?: string;
    product_id?: string;
    product_name?: string;
    product_code?: string;
    bom_code?: string;
}

interface StageMaterialsModalProps {
    isOpen: boolean;
    onClose: () => void;
    stageNumber: number;
    stageName: string;
    selectedBomComponentIds: string[];
    itemIds: string[];
    onSave: (selectedIds: string[]) => void;
}

export default function StageMaterialsModal({
    isOpen,
    onClose,
    stageNumber,
    stageName,
    selectedBomComponentIds = [],
    itemIds = [],
    onSave,
}: StageMaterialsModalProps) {
    const [bomItems, setBomItems] = useState<BOMItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isOpen) {
            loadBOMComponents();
        }
    }, [isOpen, JSON.stringify(itemIds)]);

    const loadBOMComponents = async () => {
        if (!itemIds || itemIds.length === 0) {
            setBomItems([]);
            return;
        }

        setLoading(true);
        try {
            // Strictly fetch BOMs for ONLY the item(s) selected in this routing
            const { data } = await apiClient.get<any>('/api/v1/manufacturing/bom', {
                params: {
                    itemIds: itemIds.join(','),
                    includeItems: 'true',
                    limit: 100,
                },
            });

            const boms = data?.data || [];

            // Extract components ONLY from matching BOMs belonging strictly to the selected itemIds
            const collected: BOMItem[] = [];
            const seen = new Set<string>();

            for (const b of boms) {
                // Double verify that the BOM belongs to one of the selected item IDs
                if (itemIds.length > 0 && !itemIds.includes(String(b.product_id))) {
                    continue;
                }

                if (Array.isArray(b.items)) {
                    for (const bi of b.items) {
                        const uniqueKey = `${b.id}_${bi.id || bi.item_id}`;
                        if (!seen.has(uniqueKey)) {
                            seen.add(uniqueKey);
                            collected.push({
                                id: bi.id || bi.item_id,
                                item_id: bi.item_id,
                                item_name: bi.item_name || 'Component Item',
                                item_code: bi.item_code || '',
                                item_type: bi.item_type || bi.type || 'RAW_MATERIAL',
                                quantity: Number(bi.quantity || 1),
                                unit_name: bi.unit_name || bi.unit || 'Pcs',
                                product_id: b.product_id,
                                product_name: b.product_name,
                                product_code: b.product_code,
                                bom_code: b.code,
                            });
                        }
                    }
                }
            }

            // Ensure first show Semi-Finished, then Raw Material, then etc.
            const getItemTypeRank = (type?: string): number => {
                const norm = String(type || '').toUpperCase();
                if (norm.includes('SEMI')) return 1;
                if (norm.includes('RAW')) return 2;
                if (norm.includes('CONSUMABLE')) return 3;
                if (norm.includes('PACKAGING')) return 4;
                return 5;
            };

            collected.sort((a, b) => {
                const rankDiff = getItemTypeRank(a.item_type) - getItemTypeRank(b.item_type);
                if (rankDiff !== 0) return rankDiff;
                return (a.item_name || '').localeCompare(b.item_name || '');
            });

            setBomItems(collected);

            // Normalize selected IDs: map incoming IDs to the canonical component id (one ID per material)
            const incomingIds = selectedBomComponentIds || [];
            const normalized = new Set<string>();
            for (const item of collected) {
                if (incomingIds.includes(item.id) || (item.item_id && incomingIds.includes(item.item_id))) {
                    normalized.add(item.id);
                }
            }
            setSelectedIds(Array.from(normalized));
        } catch (err) {
            console.warn('Could not load BOM components for modal:', err);
            setBomItems([]);
        } finally {
            setLoading(false);
        }
    };

    const isItemChecked = (item: BOMItem) => {
        return selectedIds.includes(item.id);
    };

    const toggleItem = (item: BOMItem) => {
        setSelectedIds((prev) => {
            if (prev.includes(item.id)) {
                return prev.filter(id => id !== item.id);
            } else {
                return [...prev, item.id];
            }
        });
    };

    const filteredItems = useMemo(() => {
        if (!search.trim()) return bomItems;
        const q = search.toLowerCase().trim();
        return bomItems.filter(
            bi =>
                (bi.item_name && bi.item_name.toLowerCase().includes(q)) ||
                (bi.item_code && bi.item_code.toLowerCase().includes(q)) ||
                (bi.product_name && bi.product_name.toLowerCase().includes(q))
        );
    }, [bomItems, search]);

    const handleSelectAll = () => {
        const allVisibleIds = filteredItems.map(i => i.id);
        const allChecked = filteredItems.length > 0 && filteredItems.every(i => isItemChecked(i));

        if (allChecked) {
            setSelectedIds((prev) =>
                prev.filter(id => !allVisibleIds.includes(id))
            );
        } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...allVisibleIds])));
        }
    };

    const handleConfirm = () => {
        // Pass single canonical ID per selected BOM material
        const uniqueIds = Array.from(new Set(selectedIds));
        onSave(uniqueIds);
        onClose();
    };

    if (!isOpen) return null;

    const hasNoItemsSelected = !itemIds || itemIds.length === 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden flex flex-col max-h-[85vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-indigo-50/30">
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold">
                                {stageNumber}
                            </span>
                            <h3 className="text-base font-bold text-slate-800">
                                {stageName || `Stage ${stageNumber}`} — BOM Materials
                            </h3>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Select the BOM materials consumed specifically at this operation stage
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <i className="ri-close-line text-lg" />
                    </button>
                </div>

                {/* Search & Actions Bar */}
                {!hasNoItemsSelected && (
                    <div className="p-4 border-b border-slate-100 flex items-center gap-3 bg-white">
                        <div className="relative flex-1">
                            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search materials by name, code, or product..."
                                className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                            />
                        </div>
                        {bomItems.length > 0 && (
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50 transition-colors whitespace-nowrap"
                            >
                                {filteredItems.length > 0 && filteredItems.every(i => isItemChecked(i))
                                    ? 'Deselect All'
                                    : 'Select All'}
                            </button>
                        )}
                    </div>
                )}

                {/* Status Indicator banner */}
                <div className="px-6 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-slate-600">
                        Selected: <strong className="text-indigo-600">{bomItems.filter(i => isItemChecked(i)).length}</strong>
                        {!hasNoItemsSelected && ` / ${bomItems.length} materials`}
                    </span>
                    {bomItems.some(i => isItemChecked(i)) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            <i className="ri-checkbox-circle-fill text-emerald-600" />
                            Stage Status: ACTIVE
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                            <i className="ri-time-line text-amber-600" />
                            Stage Status: DRAFT (Incomplete)
                        </span>
                    )}
                </div>

                {/* Materials List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {hasNoItemsSelected ? (
                        <div className="py-12 px-6 text-center">
                            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3">
                                <i className="ri-alert-line text-2xl" />
                            </div>
                            <h4 className="text-sm font-semibold text-slate-800 mb-1">
                                No Item Selected in Routing
                            </h4>
                            <p className="text-xs text-slate-500 max-w-sm mx-auto">
                                Please select at least one finished or semi-finished product in the routing header above before linking stage materials.
                            </p>
                        </div>
                    ) : loading ? (
                        <div className="py-12 text-center text-slate-400 text-xs">
                            <i className="ri-loader-4-line animate-spin text-xl mb-2 text-indigo-500 block" />
                            Loading BOM components for selected product(s)...
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <div className="py-12 px-6 text-center">
                            <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
                                <i className="ri-inbox-line text-2xl" />
                            </div>
                            <h4 className="text-sm font-semibold text-slate-700 mb-1">
                                No BOM Components Found
                            </h4>
                            <p className="text-xs text-slate-500 max-w-sm mx-auto">
                                No active Bill of Materials (BOM) components exist for the selected item(s).
                                Please create and activate a BOM for this product in{' '}
                                <strong className="text-slate-700">Manufacturing &gt; BOM</strong>.
                            </p>
                        </div>
                    ) : (
                        filteredItems.map(item => {
                            const isChecked = isItemChecked(item);
                            return (
                                <div
                                    key={item.id}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleItem(item);
                                    }}
                                    className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer select-none ${
                                        isChecked
                                            ? 'bg-indigo-50/70 border-indigo-300 shadow-sm'
                                            : 'bg-white border-slate-200 hover:bg-slate-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            readOnly
                                            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-300 pointer-events-none"
                                        />
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="text-xs font-semibold text-slate-800">
                                                    {item.item_name}
                                                </p>
                                                {item.item_type && (
                                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                                                        item.item_type.toUpperCase().includes('SEMI')
                                                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                                                            : item.item_type.toUpperCase().includes('RAW')
                                                            ? 'bg-blue-100 text-blue-800 border-blue-200'
                                                            : item.item_type.toUpperCase().includes('CONSUMABLE')
                                                            ? 'bg-purple-100 text-purple-800 border-purple-200'
                                                            : item.item_type.toUpperCase().includes('PACKAGING')
                                                            ? 'bg-slate-100 text-slate-800 border-slate-200'
                                                            : 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                                    }`}>
                                                        {item.item_type.replace(/_/g, ' ')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {item.item_code && (
                                                    <span className="text-[11px] text-slate-400 font-mono">
                                                        Code: {item.item_code}
                                                    </span>
                                                )}
                                                {item.product_name && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.2 rounded bg-indigo-100/70 text-indigo-700 font-medium">
                                                        <i className="ri-cube-line text-[9px]" />
                                                        {item.product_name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-xs font-semibold text-slate-700">
                                            {item.quantity} {item.unit_name}
                                        </span>
                                        <span className="block text-[10px] text-slate-400">per unit</span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
                    <span className="text-[11px] text-slate-500">
                        {selectedIds.length === 0
                            ? '⚠️ Saving with 0 materials leaves stage in DRAFT status'
                            : '✅ Stage will be marked ACTIVE and usable in PRD'}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-white"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            className="px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 shadow-sm transition-colors"
                        >
                            Save BOM Mapping
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
